/**
 * Internal reservation statuses after Salesforce mapping.
 * - AWAITING_CUSTOMER_SIGNATURE (Booking form Sent): check-email + Cancel/Resend
 * - UNKNOWN_RESERVED (BE status unmatched in the local CTA map):
 *   status line is the raw API string when no Contentful key matches;
 *   Cancel / Resend visible
 */
export const ReservationStatus = {
  AWAITING_CUSTOMER_SIGNATURE: 'AWAITING_CUSTOMER_SIGNATURE',
  CUSTOMER_SIGNED_BOOKING_FORM: 'CUSTOMER_SIGNED_BOOKING_FORM',
  OPPORTUNITY_NOT_CREATED: 'OPPORTUNITY_NOT_CREATED',
  OPPORTUNITY_CREATED_BEFORE_SALES_ORDER:
    'OPPORTUNITY_CREATED_BEFORE_SALES_ORDER',
  AWAITING_RESERVATION_FORM_CUSTOMER: 'AWAITING_RESERVATION_FORM_CUSTOMER',
  AWAITING_RESERVATION_FORM_BLOOM: 'AWAITING_RESERVATION_FORM_BLOOM',
  RESERVATION_FORM_SIGNED: 'RESERVATION_FORM_SIGNED',
  DECREE_APPROVAL: 'DECREE_APPROVAL',
  SPA_AWAITING_CUSTOMER: 'SPA_AWAITING_CUSTOMER',
  SPA_AWAITING_BLOOM: 'SPA_AWAITING_BLOOM',
  SPA_SIGNED: 'SPA_SIGNED',
  UNKNOWN_RESERVED: 'UNKNOWN_RESERVED',
} as const;

export type ReservationStatus =
  (typeof ReservationStatus)[keyof typeof ReservationStatus];

export const ReservationVisibleTone = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  CONFIRMED: 'confirmed',
} as const;

export type ReservationVisibleTone =
  (typeof ReservationVisibleTone)[keyof typeof ReservationVisibleTone];

export interface ReservationStatusFlags {
  readonly canCancelReservation: boolean;
  readonly canResendEmail: boolean;
}

export interface ReservationStatusConfig extends ReservationStatusFlags {
  readonly tone: ReservationVisibleTone;
}

export const RESERVATION_STATUS_CONFIG: Record<
  ReservationStatus,
  ReservationStatusConfig
> = {
  [ReservationStatus.AWAITING_CUSTOMER_SIGNATURE]: {
    canCancelReservation: true,
    canResendEmail: true,
    tone: ReservationVisibleTone.PENDING,
  },
  [ReservationStatus.CUSTOMER_SIGNED_BOOKING_FORM]: {
    canCancelReservation: true,
    canResendEmail: false,
    tone: ReservationVisibleTone.CONFIRMED,
  },
  [ReservationStatus.OPPORTUNITY_NOT_CREATED]: {
    canCancelReservation: true,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.OPPORTUNITY_CREATED_BEFORE_SALES_ORDER]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.AWAITING_RESERVATION_FORM_CUSTOMER]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.AWAITING_RESERVATION_FORM_BLOOM]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.RESERVATION_FORM_SIGNED]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.DECREE_APPROVAL]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.SPA_AWAITING_CUSTOMER]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.SPA_AWAITING_BLOOM]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.SPA_SIGNED]: {
    canCancelReservation: false,
    canResendEmail: false,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
  [ReservationStatus.UNKNOWN_RESERVED]: {
    canCancelReservation: true,
    canResendEmail: true,
    tone: ReservationVisibleTone.IN_PROGRESS,
  },
};

/** UI color token per sheet tone (unit details). Hex lives in uiColors only. */
export const RESERVATION_TONE_COLOR_TOKEN: Record<
  ReservationVisibleTone,
  | 'reservationPendingText'
  | 'reservationInProgressText'
  | 'reservationConfirmedText'
> = {
  [ReservationVisibleTone.PENDING]: 'reservationPendingText',
  [ReservationVisibleTone.IN_PROGRESS]: 'reservationInProgressText',
  [ReservationVisibleTone.CONFIRMED]: 'reservationConfirmedText',
};

export interface ReservationListingChipTokens {
  readonly text:
    | 'reservationPendingText'
    | 'reservationInProgressText'
    | 'reservationListingConfirmedText';
  readonly bg:
    | 'reservationPendingBg'
    | 'reservationInProgressBg'
    | 'reservationListingConfirmedBg';
}

/** Listing status chip fill + text. Hex lives in uiColors only. */
export const RESERVATION_TONE_LISTING_CHIP: Record<
  ReservationVisibleTone,
  ReservationListingChipTokens
> = {
  [ReservationVisibleTone.PENDING]: {
    text: 'reservationPendingText',
    bg: 'reservationPendingBg',
  },
  [ReservationVisibleTone.IN_PROGRESS]: {
    text: 'reservationInProgressText',
    bg: 'reservationInProgressBg',
  },
  [ReservationVisibleTone.CONFIRMED]: {
    text: 'reservationListingConfirmedText',
    bg: 'reservationListingConfirmedBg',
  },
};

export function getReservationVisibleTone(
  status: ReservationStatus,
): ReservationVisibleTone {
  return RESERVATION_STATUS_CONFIG[status].tone;
}

export function getReservationToneColorToken(
  status: ReservationStatus,
): (typeof RESERVATION_TONE_COLOR_TOKEN)[ReservationVisibleTone] {
  return RESERVATION_TONE_COLOR_TOKEN[getReservationVisibleTone(status)];
}

export function getReservationToneColor(
  status: ReservationStatus,
  colors: Record<
    (typeof RESERVATION_TONE_COLOR_TOKEN)[ReservationVisibleTone],
    string
  >,
): string {
  return colors[getReservationToneColorToken(status)];
}

export function getReservationListingChipTokens(
  status: ReservationStatus,
): ReservationListingChipTokens {
  return RESERVATION_TONE_LISTING_CHIP[getReservationVisibleTone(status)];
}

export function getReservationListingChipColors(
  status: ReservationStatus,
  colors: Record<
    | ReservationListingChipTokens['text']
    | ReservationListingChipTokens['bg'],
    string
  >,
): { readonly text: string; readonly bg: string } {
  const tokens = getReservationListingChipTokens(status);
  return { text: colors[tokens.text], bg: colors[tokens.bg] };
}

/** Local signature UI until Salesforce status is on the unit payload. */
export type LocalReservationSignatureStatus =
  | 'awaiting_signature'
  | 'check_email'
  | 'confirmed';

/**
 * Map resolved reservation status to the local web card visuals.
 * Awaiting signature + unmatched BE fallback → check-email + Cancel/Resend.
 * All later known statuses → confirmed (no email / Resend block).
 */
export function toLocalReservationSignatureStatus(
  status: ReservationStatus,
): LocalReservationSignatureStatus {
  if (
    status === ReservationStatus.AWAITING_CUSTOMER_SIGNATURE ||
    status === ReservationStatus.UNKNOWN_RESERVED
  ) {
    return 'check_email';
  }
  return 'confirmed';
}
